import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAppDispatch } from '../redux/hooks';
import {
    createNewUser,
    createAdminUser,
    fetchSites,
    validateUser,
    checkSuperUser,
    clearValidationError,
    clearRegistrationState,
    clearSuperUserCheck
} from '../redux/RegistrationReducer/registrationSlice';
import { fetchSkills as fetchTeams } from '../redux/TeamReducer/teamSlice';
import { fetchSkills } from '../redux/SkillReducer/skillSlice';

const SUPER_USER_SITE_CODE = 'ALL';
const DEFAULT_ADMIN_TEAM = 'A';

interface FormState {
    workdayId: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
    confirmPassword: string;
    site: string;
    team: string;
    selectedSkills: string[];
    notificationPreferences: string[];
    agreedToTerms: boolean;
    lookedAtTerms: boolean;
    showTermsDialog: boolean;
    step: 1 | 2 | 'admin-success';
    isAdmin: boolean;
    isSuperUser: boolean;
    errors: Record<string, string>;
}

export const useRegistrationForm = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [formState, setFormState] = useState<FormState>({
        workdayId: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        site: '',
        team: '',
        selectedSkills: [],
        notificationPreferences: [],
        agreedToTerms: false,
        lookedAtTerms: false,
        showTermsDialog: false,
        step: 1,
        isAdmin: false,
        isSuperUser: false,
        errors: {}
    });

    // Fetch initial data
    useEffect(() => {
        dispatch(fetchSites());
        dispatch(fetchTeams());
    }, [dispatch]);

    // Fetch skills when site changes
    useEffect(() => {
        if (formState.site && !formState.isAdmin && !formState.isSuperUser) {
            dispatch(fetchSkills(formState.site));
            setFormState(prev => ({ ...prev, selectedSkills: [] }));
        }
    }, [formState.site, formState.isAdmin, formState.isSuperUser, dispatch]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            dispatch(clearRegistrationState());
        };
    }, [dispatch]);

    // Helper function to handle super user check response
    const handleSuperUserResponse = useCallback((response: any) => {
        if (response.isSuperUser) {
            setFormState(prev => ({
                ...prev,
                isSuperUser: true,
                isAdmin: false,
                site: SUPER_USER_SITE_CODE 
            }));
            return;
        }
        
        if (response.isAdmin) {
            setFormState(prev => ({
                ...prev,
                isAdmin: true,
                isSuperUser: false
            }));
            return;
        }
        
        setFormState(prev => ({
            ...prev,
            isAdmin: false,
            isSuperUser: false
        }));
    }, []);

    // Helper function to handle super user check error
    const handleSuperUserError = useCallback(() => {
        setFormState(prev => ({
            ...prev,
            isSuperUser: false,
            isAdmin: false
        }));
    }, []);

    // Check for super user when basic fields are filled
    useEffect(() => {
        const shouldCheck =
            formState.workdayId.trim().length >= 3 &&
            formState.firstName.trim().length > 0 &&
            formState.lastName.trim().length > 0;

        if (!shouldCheck) {
            setFormState(prev => ({
                ...prev,
                isSuperUser: false,
                isAdmin: false
            }));
            dispatch(clearSuperUserCheck());
            return;
        }

        // Debounce the API call - wait 500ms after user stops typing
        const debounceTimeout = setTimeout(() => {
            dispatch(checkSuperUser({
                workdayId: formState.workdayId.trim(),
                firstName: formState.firstName.trim(),
                lastName: formState.lastName.trim()
            }))
                .unwrap()
                .then(handleSuperUserResponse)
                .catch(handleSuperUserError);
        }, 500);

        // Cleanup function to cancel the timeout if user keeps typing
        return () => clearTimeout(debounceTimeout);
    }, [formState.workdayId, formState.firstName, formState.lastName, dispatch, handleSuperUserResponse, handleSuperUserError]);

    const updateField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
        setFormState(prev => ({ ...prev, [field]: value }));
    };

    const clearError = (field: string) => {
        setFormState(prev => ({
            ...prev,
            errors: { ...prev.errors, [field]: '' }
        }));
    };

    const setErrors = (errors: Record<string, string>) => {
        setFormState(prev => ({ ...prev, errors }));
    };

    const resetForm = useCallback(() => {
        setFormState({
            workdayId: '',
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            password: '',
            confirmPassword: '',
            site: '',
            team: '',
            selectedSkills: [],
            notificationPreferences: [],
            agreedToTerms: false,
            lookedAtTerms: false,
            showTermsDialog: false,
            step: 1,
            isAdmin: false,
            isSuperUser: false,
            errors: {}
        });
    }, []); // Empty dependency array since it only uses setFormState

    // Helper function to handle validation success
    const handleValidationSuccess = useCallback((result: any) => {
        if (result?.isSuperUser === true) {
            setFormState(prev => ({ ...prev, isSuperUser: true, isAdmin: false }));
            toast.success("Welcome! Your account is being configured with super user access. Some registration steps have been streamlined");
            return;
        }
        
        if (result?.isAdmin === true) {
            setFormState(prev => ({ ...prev, isAdmin: true, isSuperUser: false }));
            toast.success("Welcome! Your account is being configured with administrator access. Some registration steps have been streamlined");
            return;
        }
        
        setFormState(prev => ({ ...prev, isAdmin: false, isSuperUser: false }));
        toast.success("Validation successful!");
    }, []);

    // Helper function to handle validation error
    const handleValidationError = useCallback((errorMessage = "User validation failed. Please check your information.") => {
        toast.error(errorMessage);

        setFormState(prev => ({
            ...prev,
            errors: {
                ...prev.errors,
                workdayId: "Please verify your Workday ID",
                firstName: "Please verify your first name",
                lastName: "Please verify your last name",
                site: "Please verify your site selection"
            }
        }));
    }, []);

    const handleContinue = async (validateStep1: () => boolean) => {
        if (!validateStep1()) {
            toast.error("Please complete all required fields");
            return;
        }

        try {
            dispatch(clearValidationError());

            const result = await dispatch(validateUser({
                workdayId: formState.workdayId.trim(),
                firstName: formState.firstName.trim(),
                lastName: formState.lastName.trim(),
                siteCd: formState.isSuperUser ? SUPER_USER_SITE_CODE : formState.site
            })).unwrap();

            handleValidationSuccess(result);
            setFormState(prev => ({ ...prev, step: 2 }));
        } catch (error: any) {
            handleValidationError(error);
        }
    };

    // Helper function to build registration payload
    const buildRegistrationPayload = useCallback(() => {
        return {
            siteCd: formState.isSuperUser ? SUPER_USER_SITE_CODE : formState.site,
            workdayId: formState.workdayId,
            firstName: formState.firstName,
            lastName: formState.lastName,
            passwordHash: formState.password,
            team: (formState.isAdmin || formState.isSuperUser) ? DEFAULT_ADMIN_TEAM : formState.team,
            notes: '',
            email: formState.email,
            phoneNbr: formState.phone.trim() || null,
            agreeToTerms: formState.agreedToTerms ? 'Y' : 'N',
            shiftEligibility: (formState.isAdmin || formState.isSuperUser) ? [] : ["VTO", "WKR", "SFT", "VTO-D", "SFT-D"],
            skills: (formState.isAdmin || formState.isSuperUser) ? [] : formState.selectedSkills,
            notifPreferences: formState.notificationPreferences,
            isAdmin: formState.isAdmin,
        };
    }, [formState]);

    // Helper function to handle admin user registration
    const handleAdminRegistration = useCallback(async (payload: any) => {
        const result = await dispatch(createAdminUser(payload)).unwrap();
        setFormState(prev => ({ ...prev, step: 'admin-success' }));
        toast.success(result?.message || "Registration initiated! Please check your email for an activation link.");
    }, [dispatch]);

    // Helper function to handle regular user registration
    const handleRegularUserRegistration = useCallback(async (payload: any) => {
        const result = await dispatch(createNewUser(payload)).unwrap();

        const isAutoActivated = result?.isAutoActivated && result?.canLoginImmediately;
        const message = isAutoActivated
            ? result?.message || "Registration successful! Your account has been automatically activated. You can log in immediately."
            : result?.message || "Thanks for registering! Your account is pending supervisor approval.";

        toast.success(message, { duration: 4000 });

        resetForm();
        setTimeout(() => {
            navigate('/login');
        }, 2500);
    }, [dispatch, navigate, resetForm]);

    const handleSubmit = async (validateStep2: () => boolean) => {
        if (!validateStep2()) {
            toast.error("Please complete all required fields before submitting");
            return;
        }

        const payload = buildRegistrationPayload();

        try {
            if (formState.isAdmin || formState.isSuperUser) {
                await handleAdminRegistration(payload);
            } else {
                await handleRegularUserRegistration(payload);
            }
        } catch (error: any) {
            console.error("Registration error:", error);
            toast.error(error?.message || error || "Registration failed. Please try again.");
        }
    };

    return {
        formState,
        updateField,
        clearError,
        setErrors,
        resetForm,
        handleContinue,
        handleSubmit,
        navigate
    };
};